// A button and the panel it opens.
//
// Two things in the masthead need exactly this -- the sync pill and the profile button -- and
// both need the dismissal contract the filter panel already set: a click anywhere else closes
// the panel, Escape closes it and hands focus back to the button that opened it. A third
// hand-rolled copy of that effect is where a keyboard user starts finding one of them broken,
// so the contract lives here once.

import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';

export function Popover({
  label,
  triggerClass,
  trigger,
  panelClass,
  children,
}: {
  // What the button announces. Both triggers are a glyph or a pair of initials, so neither has
  // an accessible name from its own content.
  label: string;
  triggerClass: string;
  trigger: ReactNode;
  panelClass?: string;
  children: ReactNode;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Same pair of listeners the filter panel uses, and for the same reason: the control that
  // opened a thing is where a keyboard user expects to land when it shuts.
  useEffect(() => {
    if (!isOpen) return;

    function onDocumentClick(event: MouseEvent) {
      if (wrapRef.current?.contains(event.target as Node)) return;
      setIsOpen(false);
    }
    function onDocumentKey(event: KeyboardEvent) {
      if (event.key !== 'Escape') return;
      setIsOpen(false);
      buttonRef.current?.focus();
    }

    document.addEventListener('click', onDocumentClick);
    document.addEventListener('keydown', onDocumentKey);
    return () => {
      document.removeEventListener('click', onDocumentClick);
      document.removeEventListener('keydown', onDocumentKey);
    };
  }, [isOpen]);

  return (
    <div className="popwrap" ref={wrapRef}>
      <button
        ref={buttonRef}
        type="button"
        className={triggerClass + (isOpen ? ' on' : '')}
        aria-label={label}
        aria-expanded={isOpen}
        onClick={() => setIsOpen(open => !open)}
      >
        {trigger}
      </button>
      {isOpen ? (
        <div className={'pop' + (panelClass ? ' ' + panelClass : '')}>{children}</div>
      ) : null}
    </div>
  );
}
